import { useEffect } from 'react';
import create from 'zustand';

import SongBadges from '../../../../components/common/SongBadges';
import OverviewPill from '../OverviewPill';
import SongChartsBadge, { hasChartsBadge } from '../SongChartsBadge';
import SongDetails from '../SongDetails';
import ViewServiceBar from '../ViewServiceBar/ViewServiceBar';
import ViewSongPlugins from '../ViewSongPlugins';
import { useGlobalContext } from '../../../../context/GlobalContext';
import { countOverviewParts } from '../../../../utils/parts';

export const useViewHeaderStore = create((set) => ({
  songKey: '',
  songId: null,
  setSongKey: (songKey, songId) => set((state) => ({ songKey, songId: songId !== undefined ? songId : state.songId })),
}));

const ViewHeader = ({
  song,
  songs,
  serviceSongs,
  popularityData,
  service,
  onPrevious,
  onNext,
  firstSong,
  lastSong,
  hideChords,
  setHideChords,
  overview,
  setOverview,
  continuousView,
  setContinuousView,
  resetAutoTranspose,
  onManualTranspose,
  currentSongIndex,
}) => {
  const storeSongKey = useViewHeaderStore((state) => state.songKey);
  const storeSongId = useViewHeaderStore((state) => state.songId);
  const setSongKey = useViewHeaderStore((state) => state.setSongKey);
  const songKey = storeSongId === song._id ? storeSongKey : song.key;
  const { showPlugins } = useGlobalContext();

  useEffect(() => {
    setSongKey(song.key, song._id);
  }, [song, setSongKey]);

  // In continuous view the header belongs to the first song but the body renders the whole
  // service, so the ratio is aggregated across every song to stay honest about what's hidden.
  const overviewCounts = continuousView
    ? (serviceSongs || songs || []).reduce(
        (totals, item) => {
          const { shown, total } = countOverviewParts(item.parts);
          return { shown: totals.shown + shown, total: totals.total + total };
        },
        { shown: 0, total: 0 },
      )
    : countOverviewParts(song.parts);

  const overviewPill = overview ? (
    <OverviewPill shown={overviewCounts.shown} total={overviewCounts.total} onExit={() => setOverview(false)} />
  ) : null;

  const chartsBadge = hasChartsBadge(song) ? <SongChartsBadge song={song} /> : null;
  // Null rather than an empty fragment when there is neither: SongBadges reads this to decide
  // whether the row is worth drawing at all
  const extraBadges =
    overviewPill || chartsBadge ? (
      <>
        {overviewPill}
        {chartsBadge}
      </>
    ) : null;

  // Sits in the service bar. In continuous view these are the first song's; each song after it
  // carries its own in SongHeaderSection
  const badges = (
    <SongBadges
      song={song}
      popularityData={popularityData}
      className="horizontal-menu"
      extraBadges={extraBadges}
      // Inside a service, the services list carries the latest edit instead
      hideUpdatedBadge={Boolean(service)}
      hidePopularityBadge={Boolean(service)}
    />
  );

  return (
    <>
      <SongDetails song={song} songKey={songKey} resetAutoTranspose={resetAutoTranspose} />

      {showPlugins && (
        <ViewSongPlugins
          hideChords={hideChords}
          setHideChords={setHideChords}
          overview={overview}
          setOverview={setOverview}
          song={song}
          service={service}
          continuousView={continuousView}
          setContinuousView={setContinuousView}
          onManualTranspose={onManualTranspose}
          currentSongIndex={currentSongIndex}
        />
      )}

      <ViewServiceBar
        onPrevious={onPrevious}
        onNext={onNext}
        firstSong={firstSong}
        lastSong={lastSong}
        service={service}
        continuousView={continuousView}
        songs={serviceSongs || songs}
        song={song}
        badges={badges}
      />
    </>
  );
};

export default ViewHeader;
